/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as appStrings from '@touchplan/strings';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { cssThemes } from 'src/app/app-data/constants/cssThemes';
import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';
import { AttachedFilesService } from '../../attached-files.service';
import { AttachmentsViewState } from '../../attachments-view-state';

@Component({
	selector: 'attached-images-tool-bar',
	templateUrl: './attached-images-tool-bar.component.html',
	styleUrls: ['./attached-images-tool-bar.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachedImagesToolBarComponent implements OnInit, OnDestroy {
	public attachmentViewState = AttachmentsViewState;
	public hideTrashIcon$: Observable<boolean>;
	public appStrings = appStrings;

	private shutdownSubject$: Subject<boolean> = new Subject();

	constructor(public dialog: MatDialog, public attachedFilesService: AttachedFilesService) {}

	public ngOnInit(): void {
		this.hideTrashIcon$ = combineLatest([
			this.attachedFilesService.editable$,
			this.attachedFilesService.viewState$,
			this.attachedFilesService.numFileSelection$,
		]).pipe(
			map((values) => {
				const editable = values[0];
				if (!editable) {
					return true;
				}
				const viewState = values[1];
				const numFileSelection = values[2];
				return viewState !== AttachmentsViewState.FileCarouselView && !numFileSelection;
			})
		);
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next(true);
		this.shutdownSubject$.unsubscribe();
	}

	public get viewState() {
		return this.attachedFilesService.viewState$.getValue();
	}

	public changeSelectionViewMode(): void {
		switch (this.viewState) {
			case this.attachmentViewState.MultiSelectMode:
				this.attachedFilesService.changeViewState(this.attachmentViewState.SingleSelectMode);
				break;
			case this.attachmentViewState.FileCarouselView:
				this.attachedFilesService.selectFile(null);
				this.attachedFilesService.changeViewState(this.attachmentViewState.SingleSelectMode);
				break;
			default:
				this.attachedFilesService.changeViewState(this.attachmentViewState.MultiSelectMode);
		}
	}

	public onDownloadClicked(): void {
		if (this.attachedFilesService.viewState$.getValue() === AttachmentsViewState.MultiSelectMode) {
			if (this.attachedFilesService.fileSelection.size === 0) {
				return;
			}
			for (const file of Array.from(this.attachedFilesService.fileSelection)) {
				this.attachedFilesService.api.downloadAttachment(file);
			}
		} else if (this.attachedFilesService.viewState$.getValue() === AttachmentsViewState.FileCarouselView) {
			this.attachedFilesService.api.downloadAttachment(this.attachedFilesService.fileSelected$.getValue());
		}
	}

	public deleteFilesClicked(): void {
		if (
			this.attachedFilesService.viewState$.getValue() === AttachmentsViewState.MultiSelectMode &&
			this.attachedFilesService.fileSelection.size === 0
		) {
			return;
		}

		const pluralize =
			this.attachedFilesService.fileSelection.size === 1 ||
			this.viewState === this.attachmentViewState.FileCarouselView
				? ''
				: 's';
		const data = {
			title: `Delete Attachment${pluralize}`,
			content: `Attachment${pluralize} will be deleted permanently for all users.`,
			yesButtonText: this.appStrings.UICONTROL_TEXT_DELETE.toUpperCase(),
			noButtonText: this.appStrings.UICONTROL_TEXT_CANCEL.toUpperCase(),
			yesButtonColor: cssThemes.warn,
		};

		this.dialog
			.open(GenericDialogComponent, { data, disableClose: true })
			.afterClosed()
			.pipe(
				filter((result) => result),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(() => {
				if (this.attachedFilesService.viewState$.getValue() === AttachmentsViewState.MultiSelectMode) {
					if (this.attachedFilesService.fileSelection.size === 0) {
						return;
					}

					for (const file of Array.from(this.attachedFilesService.fileSelection)) {
						this.attachedFilesService.api.deleteAttachment(file);
					}
					// switch to single select view when all attachments are deleted
					if (this.attachedFilesService.file$.getValue().length === 0) {
						this.attachedFilesService.changeViewState(this.attachmentViewState.SingleSelectMode);
					}
				} else if (
					this.attachedFilesService.viewState$.getValue() === AttachmentsViewState.FileCarouselView
				) {
					const currentAllFiles = this.attachedFilesService.file$.getValue();
					const currentSelection = this.attachedFilesService.fileSelected$.getValue();
					const currentIndex = currentAllFiles.findIndex((file) => file.$id === currentSelection.$id);

					// switch to single select view when all attachments are deleted
					if (currentAllFiles.length === 1) {
						this.attachedFilesService.selectFile(null);
						this.attachedFilesService.changeViewState(this.attachmentViewState.SingleSelectMode);
					} else {
						const nextIndex =
							currentIndex === currentAllFiles.length - 1 ? currentAllFiles.length - 2 : currentIndex + 1;
						const nextSelectedFile = currentAllFiles[nextIndex];
						this.attachedFilesService.selectFile(nextSelectedFile);
					}
					this.attachedFilesService.api.deleteAttachment(currentSelection);
				}
				this.attachedFilesService.clearFileSelection();
			});
	}
}
